import type { JSX } from 'react';
import type { CompanyFinancialsResource } from '@majuscule/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { internalFetch } from '../../lib/api';
import { crmKeys, crmSend, formatDate, formatInstantDate } from '../../lib/crm';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Last known revenue of a company (D-FIN.1), from the INPI (exact closing date)
 * and « Recherche d'entreprises ». A row of the « Informations » card.
 */

const SOURCE_LABEL: Record<CompanyFinancialsResource['failedSources'][number], string> = {
  inpi: 'INPI',
  recherche_entreprises: 'Annuaire des entreprises',
};

function financialsKey(companyId: string): readonly string[] {
  return [...crmKeys.company(companyId), 'financials'];
}

/** « 1,9 Md € », « 245 k€ »: the order of magnitude a salesperson needs. */
function compactEuros(amount: string): string {
  const n = Number(amount);
  return Number.isFinite(n)
    ? n.toLocaleString('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        notation: 'compact',
        maximumFractionDigits: 1,
      })
    : amount;
}

function fullEuros(amount: string): string {
  const n = Number(amount);
  return Number.isFinite(n)
    ? n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    : `${amount} €`;
}

function revenueDetail(revenue: NonNullable<CompanyFinancialsResource['revenue']>): string {
  const period =
    revenue.fiscalYearEnd === null
      ? `exercice ${String(revenue.fiscalYear)}`
      : `exercice clos le ${formatDate(revenue.fiscalYearEnd)}`;
  return `${period} · ${SOURCE_LABEL[revenue.source]}`;
}

/** A filing more recent than the revenue shown, whose figures are not public. */
function newerFilingNote(data: CompanyFinancialsResource): string | null {
  const filing = data.latestFiling;
  if (filing === null) {
    return null;
  }
  const revenueEnd = data.revenue?.fiscalYearEnd ?? null;
  const revenueYear = data.revenue?.fiscalYear ?? null;
  const newer =
    revenueEnd !== null
      ? filing.fiscalYearEnd > revenueEnd
      : revenueYear === null || Number(filing.fiscalYearEnd.slice(0, 4)) > revenueYear;
  if (!newer) {
    return null;
  }
  return `Comptes clos le ${formatDate(filing.fiscalYearEnd)} : ${
    filing.confidential ? 'confidentiels' : 'chiffres non disponibles'
  }`;
}

export function CompanyRevenue({ companyId }: { companyId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const key = financialsKey(companyId);
  const financials = useQuery({
    queryKey: key,
    queryFn: () =>
      internalFetch<CompanyFinancialsResource>(`/internal/crm/companies/${companyId}/financials`),
    staleTime: 5 * 60_000,
  });
  const refresh = useMutation({
    mutationFn: () =>
      crmSend<CompanyFinancialsResource>('POST', `/companies/${companyId}/financials/refresh`),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
    },
  });

  const data = financials.data;
  if (data === undefined) {
    return (
      <span className="text-muted-foreground">
        {financials.isError ? 'Indisponible' : 'Recherche…'}
      </span>
    );
  }
  if (data.siren === null) {
    return <span className="text-muted-foreground">SIREN requis</span>;
  }

  const note = newerFilingNote(data);
  const failed =
    data.failedSources.length > 0
      ? `${data.failedSources.map((s) => SOURCE_LABEL[s]).join(' et ')} injoignable${
          data.failedSources.length > 1 ? 's' : ''
        }`
      : null;
  const checked =
    data.checkedAt === null ? 'jamais vérifié' : `vérifié le ${formatInstantDate(data.checkedAt)}`;

  return (
    <span className="inline-flex items-start gap-1.5">
      <span className="flex flex-col items-end gap-0.5">
        {data.revenue === null ? (
          <span className="text-muted-foreground">Non publié</span>
        ) : (
          <span
            className="font-mono font-medium tabular-nums"
            title={fullEuros(data.revenue.amount)}
          >
            {compactEuros(data.revenue.amount)}
          </span>
        )}
        {data.revenue !== null && (
          <span className="text-[12px] text-muted-foreground">{revenueDetail(data.revenue)}</span>
        )}
        {note !== null && <span className="text-[12px] text-muted-foreground">{note}</span>}
        {failed !== null && <span className="text-[12px] text-destructive">{failed}</span>}
        {refresh.isError && (
          <span className="text-[12px] text-destructive">Actualisation impossible</span>
        )}
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="-my-1"
            aria-label="Actualiser le chiffre d’affaires"
            disabled={refresh.isPending}
            onClick={() => {
              refresh.mutate();
            }}
          >
            <RefreshCw
              aria-hidden="true"
              className={refresh.isPending ? 'animate-spin' : undefined}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Actualiser ({checked})</TooltipContent>
      </Tooltip>
    </span>
  );
}
